import type { Language } from '../types/lead'

/**
 * Flat key-based translation dictionary.
 * Never duplicate components per language and never inline `lang === 'es' ? ... : ...` in JSX.
 */
export const translations: Record<string, Record<Language, string>> = {
  // Header
  'nav.brand': { en: 'MIAMI CAR QUEENS', es: 'MIAMI CAR QUEENS' },
  'nav.cta': { en: 'CHECK MY OPTIONS', es: 'VER MIS OPCIONES' },
  'nav.ctaShort': { en: 'MY OPTIONS', es: 'MIS OPCIONES' },
  'nav.home': { en: 'Miami Car Queens — home', es: 'Miami Car Queens — inicio' },
  'nav.language': { en: 'Language', es: 'Idioma' },
  'nav.skip': { en: 'Skip to main content', es: 'Ir al contenido principal' },

  // Hero
  'hero.eyebrow': {
    en: 'MIAMI CAR QUEENS · GABRIELA + CHANIRA · MIAMI / DORAL, FL',
    es: 'MIAMI CAR QUEENS · GABRIELA + CHANIRA · MIAMI / DORAL, FL',
  },
  'hero.headline1': { en: 'YOUR NEXT CAR.', es: 'TU PRÓXIMO CARRO.' },
  'hero.headline2': { en: 'YOUR BEST DEAL.', es: 'TU MEJOR DEAL.' },
  'hero.sub': {
    en: 'Personal attention, options for different situations, and zero pressure. We help you find the right vehicle and understand your options.',
    es: 'Atención personal, opciones para diferentes situaciones y cero presión. Te ayudamos a encontrar el carro correcto y entender tus opciones.',
  },
  'hero.ctaPrimary': { en: 'CHECK MY OPTIONS', es: 'VER MIS OPCIONES' },
  'hero.ctaSecondary': { en: 'TALK TO THE QUEENS', es: 'HABLAR CON LAS QUEENS' },
  'hero.chip1': { en: 'New & Used', es: 'Nuevos y Usados' },
  'hero.chip2': { en: 'Leasing & Financing', es: 'Leasing y Financiamiento' },
  'hero.chip3': { en: 'Trade-Ins Welcome', es: 'Aceptamos Trade-Ins' },
  'hero.chip4': { en: 'Español & English', es: 'Español & English' },
  'hero.tagline': { en: 'NO PRESSURE. JUST THE BEST DEAL.', es: 'NO PRESSURE. JUST THE BEST DEAL.' },
  'hero.photoAlt': {
    en: 'Gabriela and Chanira, the Miami Car Queens',
    es: 'Gabriela y Chanira, las Miami Car Queens',
  },
  'hero.photo': {
    en: '[REPLACE WITH APPROVED HERO PHOTO — GABRIELA + CHANIRA, HORIZONTAL, NATURAL DAYLIGHT]',
    es: '[REEMPLAZAR CON FOTO HERO APROBADA — GABRIELA + CHANIRA, HORIZONTAL, LUZ NATURAL]',
  },

  // NOTE: the trust bar was removed — its three claims repeated the hero chips and the
  // Queen Experience panel, which made the page longer without adding an argument.

  // Meet the Queens
  'queens.eyebrow': { en: 'MEET YOUR CAR QUEENS', es: 'CONOCE A TUS CAR QUEENS' },
  'queens.title': { en: 'TWO WOMEN. ONE MISSION.', es: 'DOS MUJERES. UNA MISMA MISIÓN.' },
  'queens.support': {
    en: 'We love selling cars, but the relationships we build with our customers are what make the experience different.',
    es: 'Nos encanta vender carros, pero las relaciones que construimos con nuestros clientes son lo que hace diferente la experiencia.',
  },
  'queens.gabriela.bio': {
    en: 'Direct attention, clear communication, and a customer-first approach designed to help you understand your options before making a decision.',
    es: 'Atención directa, comunicación clara y un enfoque centrado en el cliente para ayudarte a entender tus opciones antes de decidir.',
  },
  'queens.chanira.bio': {
    en: 'Focused on a personal, comfortable experience from the first conversation through the entire process.',
    es: 'Enfocada en una experiencia personal y cómoda desde la primera conversación y durante todo el proceso.',
  },
  'queens.quote': {
    en: '“More than selling cars, we want people to feel confident about the decision they’re making.”',
    es: '“Más que vender carros, queremos que las personas se sientan seguras de la decisión que están tomando.”',
  },
  'queens.alt.gabriela': {
    en: 'Gabriela, automotive sales professional at Miami Car Queens',
    es: 'Gabriela, profesional de ventas automotrices de Miami Car Queens',
  },
  'queens.alt.chanira': {
    en: 'Chanira, automotive sales professional at Miami Car Queens',
    es: 'Chanira, profesional de ventas automotrices de Miami Car Queens',
  },
  'queens.alt.both': {
    en: 'Gabriela, on the left, and Chanira, on the right — the Miami Car Queens',
    es: 'Gabriela, a la izquierda, y Chanira, a la derecha — las Miami Car Queens',
  },
  'queens.caption': {
    en: 'Gabriela · Chanira',
    es: 'Gabriela · Chanira',
  },
  'queens.photo.gabriela': {
    en: '[REPLACE WITH APPROVED PORTRAIT — GABRIELA]',
    es: '[REEMPLAZAR CON RETRATO APROBADO — GABRIELA]',
  },
  'queens.photo.chanira': {
    en: '[REPLACE WITH APPROVED PORTRAIT — CHANIRA]',
    es: '[REEMPLAZAR CON RETRATO APROBADO — CHANIRA]',
  },
  'queens.photo.wide': {
    en: '[REPLACE WITH APPROVED GABRIELA + CHANIRA PHOTO]',
    es: '[REEMPLAZAR CON FOTO APROBADA GABRIELA + CHANIRA]',
  },

  // Queen experience
  'exp.eyebrow': { en: 'THE QUEEN EXPERIENCE', es: 'LA EXPERIENCIA QUEEN' },
  'exp.title': { en: 'TWO WAYS TO BUY A CAR.', es: 'DOS FORMAS DE COMPRAR UN CARRO.' },
  'exp.typicalTitle': { en: 'THE TYPICAL EXPERIENCE', es: 'LA EXPERIENCIA TÍPICA' },
  'exp.typical.1': { en: 'Too much pressure', es: 'Demasiada presión' },
  'exp.typical.2': { en: 'Confusing information', es: 'Información confusa' },
  'exp.typical.3': { en: 'Feeling like just another sale', es: 'Sentirte como una venta más' },
  'exp.typical.4': { en: 'Not knowing who to trust', es: 'No saber en quién confiar' },
  'exp.typical.5': {
    en: 'Getting passed from person to person',
    es: 'Que te pasen de persona en persona',
  },
  'exp.badge': { en: 'THE QUEEN WAY', es: 'THE QUEEN WAY' },
  'exp.queenTitle': { en: 'THE QUEEN EXPERIENCE', es: 'LA EXPERIENCIA QUEEN' },
  'exp.queen.1': { en: 'We listen first', es: 'Primero te escuchamos' },
  'exp.queen.2': { en: 'We explain your options clearly', es: 'Te explicamos tus opciones con claridad' },
  'exp.queen.3': { en: 'We help you compare', es: 'Te ayudamos a comparar' },
  'exp.queen.4': { en: 'Personal attention', es: 'Atención personal' },
  'exp.queen.5': {
    en: 'Support from conversation to delivery',
    es: 'Acompañamiento desde la conversación hasta la entrega',
  },
  // Each CTA names the step it actually performs — this one goes to the models.
  'exp.cta': { en: 'SEE THE MODELS →', es: 'VER LOS MODELOS →' },

  // Trade-in capture
  'tradein.title': { en: 'ALREADY HAVE A CAR?', es: '¿YA TIENES UN CARRO?' },
  'tradein.sub': {
    en: 'It may be worth more than you think. With your VIN and mileage, your Queens can check what your car is really worth.',
    es: 'Puede valer más de lo que crees. Con tu VIN y tus millas, tus Queens revisan el valor real de tu carro.',
  },
  'tradein.vin': {
    en: 'VIN (vehicle serial number) — optional',
    es: 'VIN (número de serie del carro) — opcional',
  },
  'tradein.vinOptional': {
    en: 'If you have it handy, the estimate comes out more accurate.',
    es: 'Si lo tienes a mano, el estimado sale más preciso.',
  },
  'tradein.vinWarn': {
    en: 'A VIN has 17 characters — you can leave it blank and we’ll continue without it.',
    es: 'El VIN tiene 17 caracteres — puedes dejarlo en blanco y seguimos sin él.',
  },
  'tradein.noVin': {
    en: 'No VIN? Tell us the basics instead.',
    es: '¿Sin VIN? Cuéntanos lo básico.',
  },
  'tradein.year': { en: 'Year', es: 'Año' },
  'tradein.make': { en: 'Make', es: 'Marca' },
  'tradein.model': { en: 'Model', es: 'Modelo' },
  'tradein.yearPlaceholder': { en: 'e.g. 2019', es: 'ej: 2019' },
  'tradein.makePlaceholder': { en: 'e.g. Toyota', es: 'ej: Toyota' },
  'tradein.modelPlaceholder': { en: 'e.g. Corolla', es: 'ej: Corolla' },
  'tradein.vinHelp': {
    en: '17 letters and numbers. You’ll find it on the driver-side dashboard through the windshield, on the door frame, or on your insurance card.',
    es: 'Son 17 letras y números. Lo ves en el tablero del lado del chofer a través del parabrisas, en el marco de la puerta, o en tu tarjeta del seguro.',
  },
  'tradein.vinPlaceholder': { en: 'e.g. 1HGCM82633A004352', es: 'ej: 1HGCM82633A004352' },
  'tradein.vinCount': { en: '{n} of 17', es: '{n} de 17' },
  'tradein.miles': { en: 'Current mileage', es: 'Millas actuales' },
  'tradein.milesHelp': {
    en: 'An approximate number is fine.',
    es: 'Un número aproximado está bien.',
  },
  'tradein.milesPlaceholder': { en: 'e.g. 120,000', es: 'ej: 120,000' },
  'tradein.milesReadout': { en: '{n} miles', es: '{n} millas' },
  'tradein.payoffPlaceholder': { en: 'e.g. 12,500', es: 'ej: 12,500' },
  'tradein.payoff': { en: 'How much do you still owe? (payoff)', es: '¿Cuánto le debes todavía? (payoff)' },
  'tradein.payoffHelp': {
    en: 'This is what your bank needs to close out the loan on your current car.',
    es: 'Es lo que tu banco necesita para cerrar el préstamo de tu carro actual.',
  },
  'tradein.payoffUnknown': { en: 'I don’t know my payoff', es: 'No sé mi payoff' },
  'tradein.payoffUnknownNote': {
    en: 'Your Queens will help you request it from your lender.',
    es: 'Tus Queens te ayudan a pedirlo a tu banco.',
  },
  'tradein.cta': { en: 'REVIEW MY TRADE-IN', es: 'REVISAR MI TRADE-IN' },
  'tradein.note': {
    en: 'We review it with you personally. No obligation.',
    es: 'Lo revisamos contigo personalmente. Sin compromiso.',
  },
  'tradein.err': {
    en: 'Please enter your current mileage.',
    es: 'Por favor ingresa tus millas actuales.',
  },

  // Most Wanted — models only. No prices, trims, incentives, or availability claims.
  'wanted.title': { en: 'MOST WANTED', es: 'LOS MÁS BUSCADOS' },
  'wanted.sub': {
    en: 'The models we deliver most. Tell us which one calls you.',
    es: 'Los modelos que más entregamos. Dinos cuál te llama.',
  },
  'wanted.cta': { en: 'Ask about this one →', es: 'Preguntar por este →' },
  'wanted.swipe': { en: 'Swipe to see more models.', es: 'Desliza para ver más modelos.' },

  // Hero hook. The no-figure version carries the economic promise without advertising
  // a credit term; the offer version only renders with a complete dealer-approved offer.
  'hook.eyebrow': { en: 'Monthly payment', es: 'Pago mensual' },
  'hook.title': {
    en: 'HOW MUCH CAN YOU PAY A MONTH?',
    es: '¿CUÁNTO PUEDES PAGAR AL MES?',
  },
  'hook.sub': {
    en: 'Find out in 60 seconds, with no impact to your credit',
    es: 'Descúbrelo en 60 segundos y sin afectar tu crédito',
  },
  'offer.from': { en: 'From', es: 'Desde' },
  'offer.perMo': { en: '/mo', es: '/mes' },
  'offer.eyebrowFallback': { en: 'Select Kia models', es: 'Modelos Kia selectos' },

  // Shown in place of the terms while the dealer offer sheet is still pending.
  'offer.pending': {
    en: '[PLACEHOLDER — payment not yet verified. Add model, term, down payment, APR, expiration, and the dealer disclosure in config.ts before launch.]',
    es: '[PLACEHOLDER — pago sin verificar. Agrega modelo, plazo, enganche, APR, vencimiento y el disclosure del dealer en config.ts antes de lanzar.]',
  },
  'offer.perMonth': { en: '/mo for {n} mo.', es: '/mes por {n} meses' },
  'offer.terms': {
    en: '${down} due at signing · {apr}% APR · {months} monthly payments. Subject to lender approval and credit qualification.',
    es: '${down} al firmar · {apr}% APR · {months} pagos mensuales. Sujeto a aprobación del prestamista y calificación de crédito.',
  },
  'offer.expires': { en: 'Offer ends {date}.', es: 'La oferta termina el {date}.' },

  // Friction strip. States how the Queens work — promises no outcome, price, or rate.
  'pain.heading': { en: 'How working with the Queens feels', es: 'Cómo se siente trabajar con las Queens' },
  'pain.1': { en: 'No dealership trip to start', es: 'Sin ir al dealer para empezar' },
  'pain.2': { en: 'No negotiating on your own', es: 'Sin negociar sola/o' },
  'pain.3': { en: 'No cold calls, no pressure', es: 'Sin llamadas ni presión' },
  'pain.4': { en: 'No wasted time', es: 'Sin perder tiempo' },
  'wanted.photo': {
    en: '[REPLACE WITH OFFICIAL KIA PRESS PHOTO — {model}]',
    es: '[REEMPLAZAR CON FOTO OFICIAL DE PRENSA KIA — {model}]',
  },
  'wanted.telluride': { en: 'For the whole crew', es: 'Para toda la tribu' },
  'wanted.sportage': { en: 'The everyday favorite', es: 'El favorito de todos los días' },
  'wanted.sorento': { en: 'Space without the size', es: 'Espacio sin el tamaño' },
  'wanted.k5': {
    en: 'A sedan that doesn’t feel like one',
    es: 'Un sedán que no se siente como uno',
  },
  'wanted.ev': { en: 'Electric, without compromise', es: 'Eléctrico, sin renunciar a nada' },
  'wanted.carnival': {
    en: 'All the room, none of the fuss',
    es: 'Todo el espacio, sin complicaciones',
  },

  // Payment estimator — every number is entered by the visitor
  'calc.title': { en: 'ESTIMATE YOUR PAYMENT', es: 'CALCULA TU PAGO ESTIMADO' },
  'calc.sub': {
    en: 'No experience needed. Fill in what you know and leave the rest blank — this is an estimate to orient you, not an offer, an approval, or an available rate.',
    es: 'No necesitas saber de finanzas. Llena lo que sepas y deja el resto en blanco — es una estimación para orientarte, no una oferta, ni una aprobación, ni una tasa disponible.',
  },
  'calc.price': { en: 'Price of the car you want', es: 'Precio del carro que quieres' },
  'calc.help.price': {
    en: 'What the car costs. If you don’t know yet, use a rough number.',
    es: 'Lo que cuesta el carro. Si todavía no sabes, pon un número aproximado.',
  },
  'calc.down': { en: 'Money you put down upfront', es: 'Dinero que das al principio (enganche)' },
  'calc.help.down': {
    en: 'Cash out of your pocket on day one. The more you put down, the less you pay each month. It can be $0.',
    es: 'Es el dinero de tu bolsillo el primer día. Mientras más des, menos pagas cada mes. Puede ser $0.',
  },
  'calc.tradeValue': {
    en: 'What they give you for your current car',
    es: 'Lo que te dan por tu carro actual',
  },
  'calc.help.tradeValue': {
    en: 'The value of your trade-in. Leave it blank if you don’t know — the Queens figure it out with your VIN.',
    es: 'El valor de tu trade-in. Déjalo en blanco si no lo sabes — las Queens lo averiguan con tu VIN.',
  },
  'calc.owed': { en: 'What you still owe on it', es: 'Lo que todavía le debes' },
  'calc.help.owed': {
    en: 'Your payoff — what the bank needs to close out that loan.',
    es: 'Tu payoff — lo que el banco necesita para cerrar ese préstamo.',
  },
  'calc.apr': { en: 'Interest rate (APR)', es: 'Tasa de interés (APR)' },
  'calc.help.apr': {
    en: 'What the bank charges you per year. If you don’t know it, leave it blank.',
    es: 'Lo que el banco te cobra al año. Si no la sabes, déjala en blanco.',
  },
  'calc.term': { en: 'How many months to pay it', es: 'En cuántos meses lo pagas' },
  'calc.help.term': {
    en: 'More months means a lower monthly payment, but you pay more interest overall.',
    es: 'Más meses = pago mensual más bajo, pero pagas más interés al final.',
  },
  'calc.ph.price': { en: 'e.g. 28,000', es: 'ej: 28,000' },
  'calc.ph.down': { en: 'e.g. 3,000', es: 'ej: 3,000' },
  'calc.ph.tradeValue': { en: 'e.g. 9,500', es: 'ej: 9,500' },
  'calc.ph.owed': { en: 'e.g. 12,500', es: 'ej: 12,500' },
  'calc.ph.apr': { en: 'e.g. 8.9', es: 'ej: 8.9' },
  'calc.unknown': {
    en: 'Don’t know a number? Leave it blank. Your Queens complete it with you.',
    es: '¿No sabes algún dato? Déjalo en blanco. Tus Queens lo completan contigo.',
  },
  'calc.monthsWord': { en: 'months', es: 'meses' },
  'calc.perMonth': { en: 'mo', es: 'mes' },
  'calc.result': { en: 'Estimated monthly payment', es: 'Pago mensual estimado' },
  'calc.financed': { en: 'Amount financed', es: 'Monto a financiar' },
  'calc.equityPositive': { en: 'Estimated positive equity', es: 'Equidad positiva estimada' },
  'calc.equityPositiveNote': {
    en: 'Your car may cover part of the new one. We confirm the real number with you.',
    es: 'Tu carro podría cubrir parte del nuevo. El número real lo confirmamos contigo.',
  },
  'calc.equityNegative': { en: 'You may be upside down by', es: 'Podrías estar upside down por' },
  'calc.equityNegativeNote': {
    en: 'That balance can be rolled into the new loan or paid separately. It is common and we walk you through the options.',
    es: 'Ese saldo puede sumarse al nuevo préstamo o pagarse aparte. Es común y te explicamos las opciones.',
  },
  'calc.empty': {
    en: 'Enter a vehicle price to see the estimate.',
    es: 'Ingresa un precio para ver la estimación.',
  },
  'calc.cta': { en: 'TALK ABOUT MY NUMBERS', es: 'HABLAR DE MIS NÚMEROS' },
  'calc.disclaimer': {
    en: 'Estimate only. The figures are the ones you typed and do not represent an offer, an approval, a credit decision, or a rate available to you. Taxes, tags, dealer charges, and optional products are not included. Your actual payment depends on the vehicle, your credit, the lender, and the programs in effect at the time. Nothing you enter here is sent anywhere.',
    es: 'Solo una estimación. Las cifras son las que tú escribiste y no representan una oferta, una aprobación, una decisión de crédito ni una tasa disponible para ti. No incluye impuestos, placas, cargos del concesionario ni productos opcionales. Tu pago real depende del vehículo, tu crédito, el prestamista y los programas vigentes. Nada de lo que escribes aquí se envía a ningún lado.',
  },

  // Kia warranty (verified manufacturer information)
  'warranty.title': { en: 'KIA FACTORY WARRANTY', es: 'GARANTÍA DE FÁBRICA KIA' },
  'warranty.text': {
    en: 'New Kia vehicles include a 10-year / 100,000-mile powertrain warranty and a 5-year / 60,000-mile basic warranty.',
    es: 'Los vehículos Kia nuevos incluyen garantía de fábrica de 10 años / 100,000 millas en tren motriz y 5 años / 60,000 millas básica.',
  },
  'warranty.small': {
    en: 'All warranties and roadside assistance are limited. Confirm the details with the dealership.',
    es: 'Todas las garantías y la asistencia en carretera son limitadas. Consulta los detalles con el concesionario.',
  },

  // Lead form
  'form.title': { en: 'LET’S FIND YOUR BEST OPTION', es: 'VAMOS A ENCONTRAR TU MEJOR OPCIÓN' },
  'form.sub': {
    en: 'Takes less than 60 seconds. No obligation.',
    es: 'Toma menos de 60 segundos. Sin compromiso.',
  },
  'form.firstName': { en: 'First name', es: 'Nombre' },
  'form.phone': { en: 'Phone', es: 'Teléfono' },
  'form.email': { en: 'Email', es: 'Email' },
  'form.optional': { en: 'optional', es: 'opcional' },
  'form.vehicle': { en: 'What are you looking for?', es: '¿Qué estás buscando?' },
  'form.intent': { en: 'What would you like to do?', es: '¿Qué te gustaría hacer?' },
  'form.credit': { en: 'How would you describe your credit?', es: '¿Cómo describirías tu crédito?' },
  'form.choose': { en: 'Select an option', es: 'Selecciona una opción' },
  'form.opt.suv': { en: 'SUV', es: 'SUV' },
  'form.opt.sedan': { en: 'Sedan', es: 'Sedán' },
  'form.opt.family': { en: 'Family', es: 'Familiar' },
  'form.opt.hybrid-ev': { en: 'Hybrid / EV', es: 'Híbrido / EV' },
  'form.opt.not-sure': { en: 'Not sure', es: 'No estoy segura/o' },
  // Model options, pre-selected when a visitor picks a card in "Most Wanted".
  'form.opt.telluride': { en: 'Telluride', es: 'Telluride' },
  'form.opt.sportage': { en: 'Sportage', es: 'Sportage' },
  'form.opt.sorento': { en: 'Sorento', es: 'Sorento' },
  'form.opt.k5': { en: 'K5', es: 'K5' },
  'form.opt.ev': { en: 'EV6 / EV9', es: 'EV6 / EV9' },
  'form.opt.carnival': { en: 'Carnival', es: 'Carnival' },
  'form.opt.buy': { en: 'Buy', es: 'Comprar' },
  'form.opt.lease': { en: 'Lease', es: 'Leasing' },
  'form.opt.trade-in-buy': { en: 'Trade-In + Buy', es: 'Trade-In + Comprar' },
  'form.opt.excellent': { en: 'Excellent', es: 'Excelente' },
  'form.opt.good': { en: 'Good', es: 'Bueno' },
  'form.opt.fair': { en: 'Fair', es: 'Regular' },
  'form.opt.rebuilding': { en: 'Rebuilding', es: 'En reconstrucción' },
  'form.opt.first-time': { en: 'First-Time Buyer', es: 'Primera compra' },
  'form.consent': {
    en: 'By submitting, you authorize MIAMI CAR QUEENS LLC to text/call the number above for promotional messages, possibly using automated means. Msg/data rates apply, msg frequency varies. Consent is not a condition of purchase. See terms and privacy policy. Text HELP for help and STOP to unsubscribe.',
    es: 'Al enviar, autorizas a MIAMI CAR QUEENS LLC a enviarte mensajes de texto/llamadas al número anterior para mensajes promocionales, posiblemente utilizando medios automatizados. Aplican tarifas de msj/datos, la frecuencia de msj varía. El consentimiento no es una condición de compra. Consulta los términos y la política de privacidad. Envía HELP para ayuda y STOP para cancelar la suscripción.',
  },
  'form.privacy': { en: 'Privacy Policy', es: 'Política de Privacidad' },
  'form.terms': { en: 'Terms', es: 'Términos' },
  'form.submit': { en: 'SHOW ME MY OPTIONS', es: 'VER MIS OPCIONES' },
  'form.sending': { en: 'SENDING…', es: 'ENVIANDO…' },
  'form.note': {
    en: 'Your information is private. No obligation.',
    es: 'Tu información es privada. Sin compromiso.',
  },
  'form.err.firstName': { en: 'Please enter your first name.', es: 'Por favor escribe tu nombre.' },
  'form.err.phone': {
    en: 'Please enter a valid phone number.',
    es: 'Por favor escribe un teléfono válido.',
  },
  'form.err.email': { en: 'Please enter a valid email.', es: 'Por favor escribe un email válido.' },
  'form.err.consent': {
    en: 'Please accept the consent to continue.',
    es: 'Por favor acepta el consentimiento para continuar.',
  },
  'form.err.submit': {
    en: 'We couldn’t send your information. Please try again or message a Queen directly.',
    es: 'No pudimos enviar tu información. Intenta de nuevo o escríbele directo a una Queen.',
  },
  'form.retry': { en: 'TRY AGAIN', es: 'INTENTAR DE NUEVO' },

  // Success
  'success.title': { en: 'THANK YOU, {name}.', es: 'GRACIAS, {name}.' },
  'success.sub': {
    en: 'We already have your information. One of our Queens will contact you shortly.',
    es: 'Ya tenemos tu información. Próximamente una de nuestras Queens te contactará.',
  },

  // How it works
  'how.title': { en: 'WHAT HAPPENS NEXT', es: 'QUÉ PASA DESPUÉS' },
  'how.sub': {
    en: 'Three steps. The first one takes 60 seconds.',
    es: 'Tres pasos. El primero toma 60 segundos.',
  },
  'how.cta': { en: 'START STEP 1', es: 'EMPEZAR CON EL PASO 1' },
  'how.1.title': { en: 'TELL US WHAT YOU NEED', es: 'CUÉNTANOS QUÉ NECESITAS' },
  'how.2.title': { en: 'EXPLORE YOUR OPTIONS', es: 'EXPLORA TUS OPCIONES' },
  'how.3.title': { en: 'PICK UP YOUR KEYS', es: 'RECIBE TUS LLAVES' },

  // FAQ
  'faq.title': { en: 'FREQUENT QUESTIONS', es: 'PREGUNTAS FRECUENTES' },
  'faq.q1': { en: 'Do I need perfect credit?', es: '¿Necesito crédito perfecto?' },
  'faq.q2': {
    en: 'Can I buy while rebuilding my credit?',
    es: '¿Puedo comprar mientras reconstruyo mi crédito?',
  },
  'faq.q3': { en: 'New and used?', es: '¿Nuevos y usados?' },
  'faq.q4': { en: 'Can I lease?', es: '¿Puedo hacer leasing?' },
  // NOTE: the old 'faq.q5' (can I trade in my current vehicle) was merged into 'faq.q9',
  // which asks the same thing in the customer's own words.
  'faq.q6': { en: 'Can I start on WhatsApp?', es: '¿Puedo empezar por WhatsApp?' },
  'faq.q7': { en: 'Do you speak Spanish?', es: '¿Hablan español?' },
  'faq.q8': { en: 'Where are you located?', es: '¿Dónde están ubicadas?' },
  'faq.generic': {
    en: 'Options vary based on the vehicle, your situation, eligibility, and the programs available at the time. Send us your information and we’ll walk you through what applies to you.',
    es: 'Las opciones varían según el vehículo, tu situación, la elegibilidad y los programas disponibles en ese momento. Envíanos tu información y te explicamos qué aplica en tu caso.',
  },
  'faq.a6': {
    en: 'Yes. You can start the conversation on WhatsApp with the Queen you choose, and continue at your own pace.',
    es: 'Sí. Puedes empezar la conversación por WhatsApp con la Queen que elijas y continuar a tu ritmo.',
  },
  'faq.a7': {
    en: 'Yes — Gabriela and Chanira assist you in Spanish and English.',
    es: 'Sí — Gabriela y Chanira te atienden en español e inglés.',
  },
  'faq.a8.base': {
    en: 'Gabriela and Chanira are automotive sales professionals at Doral Kia, in Doral, Florida.',
    es: 'Gabriela y Chanira son profesionales de ventas automotrices en Doral Kia, en Doral, Florida.',
  },
  'faq.q9': {
    en: 'Do you take my current car as part of the payment?',
    es: '¿Aceptan mi carro actual como parte de pago?',
  },
  'faq.a9': {
    en: 'Yes, trade-ins are evaluated. What your vehicle is worth depends on the vehicle itself and its condition, so we review it with you before anything is decided.',
    es: 'Sí, los trade-ins se evalúan. Lo que vale tu vehículo depende del vehículo y de su condición, así que lo revisamos contigo antes de decidir nada.',
  },
  'faq.q10': {
    en: 'Do you have certified pre-owned vehicles?',
    es: '¿Tienen vehículos usados certificados?',
  },
  'faq.a10': {
    en: 'Yes, Kia Certified Pre-Owned is available. The specifics vary by inventory at the time.',
    es: 'Sí, hay Kia Certified Pre-Owned disponible. Los detalles varían según el inventario del momento.',
  },
  'faq.q11': { en: 'Are there special programs?', es: '¿Hay programas especiales?' },
  'faq.a11': {
    en: 'Manufacturer incentive programs exist, including offers for active military. Eligibility and availability change, so we confirm what applies to you.',
    es: 'Existen programas de incentivos del fabricante, incluyendo ofertas para militares activos. La elegibilidad y disponibilidad cambian, así que confirmamos qué aplica en tu caso.',
  },

  // Final CTA
  'final.title': { en: 'READY FOR YOUR NEXT CAR?', es: '¿LISTO PARA TU PRÓXIMO CARRO?' },
  'final.photo': {
    en: '[REPLACE WITH FINAL GABRIELA + CHANIRA PHOTO]',
    es: '[REEMPLAZAR CON FOTO FINAL GABRIELA + CHANIRA]',
  },

  // Queen picker
  'picker.title': { en: 'Who would you like to talk to?', es: '¿Con quién quieres hablar?' },
  'picker.sub': {
    en: 'Pick a Queen and continue the conversation on WhatsApp.',
    es: 'Elige una Queen y continúa la conversación por WhatsApp.',
  },
  'picker.button': { en: 'WhatsApp', es: 'WhatsApp' },
  'picker.close': { en: 'Close', es: 'Cerrar' },
  'picker.unavailable': {
    en: 'WhatsApp not configured yet',
    es: 'WhatsApp aún no configurado',
  },
  'picker.msg.named': {
    en: 'Hi Queens, I’m {name}. I just submitted my information on miamicarqueen.com. I’m looking for {vehicle} and would like to learn about my options.',
    es: 'Hola Queens, soy {name}. Acabo de enviar mi información en miamicarqueen.com. Estoy buscando {vehicle} y quisiera conocer mis opciones.',
  },
  'picker.msg.generic': {
    en: 'Hi Queens, I found you on miamicarqueen.com and would like to learn about my options for my next vehicle.',
    es: 'Hola Queens, las encontré en miamicarqueen.com y quisiera conocer mis opciones para mi próximo carro.',
  },

  // Sticky CTA
  'sticky.cta': { en: 'TALK TO THE QUEENS', es: 'HABLAR CON LAS QUEENS' },

  // Footer
  'footer.services': {
    en: 'NEW · USED · LEASING · FINANCING',
    es: 'NUEVOS · USADOS · LEASING · FINANCIAMIENTO',
  },
  'footer.location': { en: 'Miami / Doral, Florida', es: 'Miami / Doral, Florida' },
  'footer.people': { en: 'Gabriela + Chanira', es: 'Gabriela + Chanira' },
  'footer.instagram': { en: 'Instagram', es: 'Instagram' },
  'footer.dealerLabel': {
    en: 'Gabriela and Chanira sell at',
    es: 'Gabriela y Chanira venden en',
  },
  'footer.notDealer': {
    en: 'Miami Car Queens is the personal brand of two automotive sales professionals. It is not a dealership.',
    es: 'Miami Car Queens es la marca personal de dos profesionales de ventas automotrices. No es un concesionario.',
  },
}

export function translate(key: string, lang: Language): string {
  const entry = translations[key]
  if (!entry) return key
  return entry[lang]
}
